import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/Button";
import { ROUTES } from "../../utils/constants";
import { useTheme } from "../../contexts/ThemeContext";
import {
  MessageSquare,
  PenLine,
  BookMarked,
  Music,
  Moon,
  Sun,
  Sparkles,
  Mic,
  Target,
  BarChart3,
  ShieldCheck,
  ArrowRight,
} from "lucide-react";

const MODULES = [
  {
    title: "Conversational Learning",
    icon: MessageSquare,
    desc: "Scenario-based role-play and multi-character dialogues for real speaking confidence.",
    bullets: ["Role-play situations", "Pronunciation support", "Confidence building"],
  },
  {
    title: "Creative Expression",
    icon: PenLine,
    desc: "Topic-based writing and voice input with instant feedback to improve clarity and grammar.",
    bullets: ["Writing prompts", "Voice-to-text option", "Mistake-friendly feedback"],
  },
  {
    title: "Animated Stories",
    icon: BookMarked,
    desc: "Age-appropriate narratives for listening, comprehension, and vocabulary growth.",
    bullets: ["Story-based learning", "Listening practice", "New words in context"],
  },
  {
    title: "Animated Rhymes",
    icon: Music,
    desc: "Phonetics and rhythm practice with memorable patterns that boost fluency.",
    bullets: ["Sounds & rhythm", "Repeat-and-learn", "Kid-friendly practice"],
  },
];

const HIGHLIGHTS = [
  {
    title: "AI Tutor Guidance",
    icon: Sparkles,
    desc: "Friendly guidance that adapts to each learner’s pace and level.",
  },
  {
    title: "Voice Practice",
    icon: Mic,
    desc: "Speak Tamil regularly with safe, classroom-friendly conversations.",
  },
  {
    title: "Can-Do Goals",
    icon: Target,
    desc: "Track progress by skills (Can-Do statements) instead of only scores.",
  },
  {
    title: "Analytics",
    icon: BarChart3,
    desc: "Teachers and parents can see growth signals over time.",
  },
  {
    title: "Safety-by-design",
    icon: ShieldCheck,
    desc: "Role-based access, kid-safe UI, and privacy-first thinking.",
  },
];

export const ModulesPage: React.FC = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();

  return (
    <div className="min-h-screen bg-app-bg dark:bg-gray-950 text-app-text dark:text-gray-100">
      {/* ✅ Same header style as Privacy page */}
      <header className="sticky top-0 z-50 border-b border-app-border/40 dark:border-gray-800 bg-app-surface/70 dark:bg-gray-900/70 backdrop-blur-md">
        <div className="mx-auto grid max-w-6xl grid-cols-[auto_1fr_auto] items-center gap-4 px-4 h-[72px] sm:px-6 lg:px-8">
          {/* LEFT: Logo */}
          <Link
            to={ROUTES.LANDING}
            className="flex items-center gap-3 rounded-xl px-2 py-2 hover:bg-app-surface-muted dark:hover:bg-gray-800 transition-colors"
            aria-label="TKM Home"
          >
            <span
              className="
                relative flex h-12 w-12 items-center justify-center
                rounded-2xl overflow-hidden
                bg-white ring-1 ring-black/10
                shadow-[0_10px_24px_rgba(0,0,0,0.18)]
              "
            >
              <img
                src="/images/tkm-logo.png"
                alt="TKM Logo"
                className="h-full w-full object-contain p-1"
                draggable={false}
              />
            </span>

            <div className="hidden md:block leading-tight text-left">
              <div className="text-base font-extrabold text-app-text dark:text-white">
                Tamil Katral Maiyam
              </div>
              <div className="text-xs font-bold text-app-text-muted dark:text-gray-400">
                Modules
              </div>
            </div>
          </Link>

          {/* CENTER: Page title (mobile) */}
          <div className="md:hidden text-center">
            <div className="text-sm font-extrabold text-app-text dark:text-white">
              Modules
            </div>
          </div>

          {/* RIGHT: Actions */}
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={toggleTheme}
              className="p-2 rounded-lg text-app-text-muted dark:text-gray-400 hover:bg-app-surface-muted dark:hover:bg-gray-800 hover:text-app-text dark:hover:text-white transition-colors"
              aria-label="Toggle theme"
            >
              {theme === "dark" ? (
                <Sun className="h-5 w-5" />
              ) : (
                <Moon className="h-5 w-5" />
              )}
            </button>

            <Button
              variant="outline"
              size="sm"
              className="border-app-border dark:border-gray-700"
              onClick={() => navigate(`${ROUTES.LOGIN}?mode=user`)}
            >
              Log in
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-12 sm:py-16">
        {/* Hero */}
        <div className="rounded-[28px] border border-app-border/60 dark:border-gray-800 bg-app-surface dark:bg-gray-900/60 p-6 sm:p-10 shadow-soft">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-app-text dark:text-white">
            Core Learning Modules
          </h1>
          <p className="mt-3 text-app-text-muted dark:text-gray-400 max-w-3xl leading-relaxed">
            Explore the four main modules of the AI Tamil Learning Bot. Each module is
            designed to build Tamil fluency through practice — speaking, listening,
            reading, and writing — in a kid-safe, classroom-friendly way.
          </p>

          <div className="mt-6 flex flex-wrap gap-3">
            <Button onClick={() => navigate(`${ROUTES.LOGIN}?mode=user`)}>
              Start learning
            </Button>
            <Button variant="outline" onClick={() => navigate(ROUTES.LANDING)}>
              Back to home
            </Button>
          </div>
        </div>

        {/* Modules */}
        <section className="mt-10">
          <div className="flex items-end justify-between gap-4">
            <div>
              <h2 className="text-2xl font-extrabold text-app-text dark:text-white">
                What students will use
              </h2>
              <p className="mt-2 text-app-text-muted dark:text-gray-400">
                Four modules that work together to build fluency.
              </p>
            </div>
          </div>

          <div className="mt-6 grid gap-6 sm:grid-cols-2">
            {MODULES.map((m) => (
              <div
                key={m.title}
                className="rounded-2xl border border-app-border dark:border-gray-800 bg-app-surface dark:bg-gray-900/60 p-6 shadow-soft"
              >
                <div className="flex items-center gap-3">
                  <div className="rounded-2xl border border-app-border dark:border-gray-800 bg-app-surface-muted dark:bg-gray-800/60 p-3">
                    <m.icon className="h-8 w-8 text-primary-600 dark:text-primary-400" />
                  </div>
                  <h3 className="text-xl font-extrabold text-app-text dark:text-white">
                    {m.title}
                  </h3>
                </div>

                <p className="mt-3 text-app-text-muted dark:text-gray-400 leading-relaxed">
                  {m.desc}
                </p>

                <ul className="mt-4 space-y-2 text-sm text-app-text-muted dark:text-gray-400">
                  {m.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2">
                      <span className="mt-[7px] h-2 w-2 rounded-full bg-primary-600 dark:bg-primary-400" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-5">
                  <button
                    type="button"
                    onClick={() => navigate(`${ROUTES.LOGIN}?mode=user`)}
                    className="inline-flex items-center gap-2 text-sm font-extrabold text-primary-700 dark:text-primary-300 hover:underline"
                  >
                    Open module <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Highlights */}
        <section className="mt-12">
          <h2 className="text-2xl font-extrabold text-app-text dark:text-white">
            Platform highlights
          </h2>
          <p className="mt-2 text-app-text-muted dark:text-gray-400 max-w-3xl">
            More than content — a complete learning experience for students and visibility
            for teachers/parents.
          </p>

          <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {HIGHLIGHTS.map((h) => (
              <div
                key={h.title}
                className="rounded-2xl border border-app-border dark:border-gray-800 bg-app-surface dark:bg-gray-900/60 p-6 shadow-soft"
              >
                <div className="flex items-center gap-3">
                  <div className="rounded-2xl border border-app-border dark:border-gray-800 bg-app-surface-muted dark:bg-gray-800/60 p-3">
                    <h.icon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                  </div>
                  <p className="font-extrabold text-app-text dark:text-white">{h.title}</p>
                </div>
                <p className="mt-3 text-sm text-app-text-muted dark:text-gray-400 leading-relaxed">
                  {h.desc}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="mt-12">
          <div className="rounded-[28px] border border-app-border/60 dark:border-gray-800 bg-app-surface dark:bg-gray-900/60 p-6 sm:p-10 shadow-soft flex flex-col sm:flex-row gap-6 sm:items-center sm:justify-between">
            <div>
              <h3 className="text-2xl font-extrabold text-app-text dark:text-white">
                Ready to explore TKM?
              </h3>
              <p className="mt-2 text-app-text-muted dark:text-gray-400 max-w-2xl">
                Log in to continue learning, or return to the home page to explore more.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button onClick={() => navigate(`${ROUTES.LOGIN}?mode=user`)}>
                Log in
              </Button>
              <Button variant="outline" onClick={() => navigate(ROUTES.LANDING)}>
                Back to home
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};
